import type { DocumentExtraction } from '../document-extraction.js';
import type { DocumentStatus, DocumentType } from '../generated/enums.js';

/**
 * Document response for list view
 * Provides basic document information with extracted summary fields
 */
export interface DocumentResponse {
  documentId: string;
  tenantId: string;
  entityId: string;
  originalName: string;
  mimeType: string;
  fileSizeBytes: number;
  processingStatus: DocumentStatus;
  documentType: DocumentType;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  /** Linked AP bill when the document is attached to a bill. */
  billId?: string | null;

  // Extracted summary fields (for list view)
  vendorName?: string;
  amount?: number;
  dueDate?: string;
}

/**
 * Browser upload from the Bills list upload flow — bills created from extracted data should start as SUBMITTED.
 * Stored on `ApDocument.metadata.uploadOrigin` together with `destination: 'bill'`.
 */
export const AP_DOCUMENT_UPLOAD_ORIGIN_BILLS_PAGE_BROWSER = 'bills_page_browser' as const;

/** Stored on `ApDocument.metadata.billAttachmentType` when the document is linked to a bill. */
export const ApDocumentBillAttachment = {
  CREATED: 'CREATED',
  LINKED: 'LINKED',
} as const;

export type ApDocumentBillAttachment =
  (typeof ApDocumentBillAttachment)[keyof typeof ApDocumentBillAttachment];

/**
 * Document detail response with full extraction data
 * Extends DocumentResponse with complete extracted data and blob URL
 */
export interface DocumentDetailResponse extends DocumentResponse {
  extractedData?: DocumentExtraction;
  ocrText?: string | null;
  processingAttempts: number;
  /** Temporary SAS URL for viewing; null when the document has no blob (e.g. email-body-only). */
  blobUrl: string | null;
  /** Primary reimbursement request linked on document metadata (standalone intake / ALC-755). */
  reimbursementRequestId?: string | null;
}

/**
 * Document list response with pagination
 */
export interface DocumentListResponse {
  documents: DocumentResponse[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Upload document response - single document
 */
export interface UploadedDocumentResponse {
  documentId: string;
  originalName: string;
  mimeType: string;
  fileSizeBytes: number;
  isDuplicate: boolean;
  status: 'PENDING' | 'DUPLICATE' | 'FAILED';
  /** When this upload matched an existing document (`DUPLICATE`), bill linked to that document if any. */
  existingBillId?: string | null;
  /** Reimbursement requests that already used this exact document before the current upload. */
  duplicateReimbursementRequestIds?: string[];
  /** Primary reimbursement request for this upload (intake / duplicate reuse). */
  reimbursementRequestId?: string;
  /** Current bill-link outcome for duplicate/manual bill upload flows. */
  billLinkStatus?: 'LINKABLE' | 'LINKED' | 'LINK_FAILED' | 'LINKED_ELSEWHERE';
}

/**
 * Failed document upload
 */
export interface FailedDocumentUpload {
  originalName: string;
  error: string;
}

/**
 * Upload documents response - batch upload result
 */
export interface UploadDocumentsResponse {
  bundleId: string;
  documents: UploadedDocumentResponse[];
  failedDocuments?: FailedDocumentUpload[];
  totalUploaded: number;
  totalDuplicates: number;
  totalFailed: number;
}

/**
 * Request for updating document (edit extracted data)
 */
export interface UpdateDocumentRequest {
  extractedData: DocumentExtraction;
}

/**
 * Processing status item
 */
export interface ProcessingStatusItem {
  documentId: string;
  /** Linked AP bill when the document is attached to a bill. */
  billId?: string;
  originalName: string;
  processingStatus: DocumentStatus;
  progress: number;
  errorMessage?: string;
  createdAt: string;
  updatedAt?: string;
  /** Standalone reimbursement routing (ALC-755): primary request id from document metadata. */
  reimbursementRequestId?: string;
  /** Other reimbursement requests that previously used this document (metadata). */
  duplicateReimbursementRequestIds?: string[];
  /** Bill on a duplicate match when `billId` is not set on this document row yet. */
  existingBillId?: string | null;
}

/**
 * Processing status response
 */
export interface ProcessingStatusResponse {
  processing: ProcessingStatusItem[];
  failed: ProcessingStatusItem[];
  totalProcessing: number;
  totalFailed: number;
}

export interface CancelDocumentProcessingResponse {
  /** Whether the document was soft-deleted and removed from the processing queue */
  cancelled: boolean;
  documentId: string;
}

/**
 * Query parameters for finding documents
 */
export interface FindDocumentsQuery {
  entityId?: string;
  status?: DocumentStatus;
  documentType?: DocumentType;
  search?: string;
  limit?: number;
  offset?: number;
}
